import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  destinationsApi,
  destinationsKeys,
  type DestinationPayload,
} from '@/features/destinations/api/destinations.api';
import { INDIA_STATES } from '@/utils/constants';
import type { Destination } from '@/types';

interface LiveCityFormDialogProps {
  open: boolean;
  /** The city being edited; null when adding a new one. */
  city: Destination | null;
  onClose: () => void;
}

function errorMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback
  );
}

export function LiveCityFormDialog({ open, city, onClose }: LiveCityFormDialogProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEdit = Boolean(city);

  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [highlight, setHighlight] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [isLive, setIsLive] = useState(true);
  const [image, setImage] = useState<{ url: string; publicId?: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(city?.name ?? '');
    setRegion(city?.region ?? '');
    setHighlight(city?.highlight ?? '');
    setSortOrder(String(city?.sortOrder ?? 0));
    setIsLive(city ? Boolean(city.isLive) : true);
    setImage(city?.imageUrl ? { url: city.imageUrl, publicId: city.imagePublicId } : null);
    setFormError(null);
  }, [open, city]);

  const saveMutation = useMutation({
    mutationFn: (payload: DestinationPayload) =>
      city ? destinationsApi.update(city._id, payload) : destinationsApi.create(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: destinationsKeys.all });
      onClose();
    },
    onError: (err) => setFormError(errorMessage(err, 'Could not save the city')),
  });

  const handleUpload = async (file: File) => {
    setUploading(true);
    setFormError(null);
    try {
      const uploaded = await destinationsApi.uploadImage(file);
      setImage({ url: uploaded.url, publicId: uploaded.publicId });
    } catch (err) {
      setFormError(errorMessage(err, 'Image upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 2) {
      setFormError('Enter a city name');
      return;
    }
    if (isLive && !image) {
      setFormError('Upload an image before putting the city live');
      return;
    }
    const order = Math.trunc(Number(sortOrder));
    saveMutation.mutate({
      name: name.trim(),
      // On create an empty state is omitted so adopting an existing record keeps its state.
      region: isEdit ? region : region || undefined,
      highlight: highlight.trim(),
      sortOrder: Number.isFinite(order) ? Math.min(9999, Math.max(0, order)) : 0,
      isLive,
      ...(image && { imageUrl: image.url, imagePublicId: image.publicId }),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ component: 'form', onSubmit: handleSubmit }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>{isEdit ? 'Edit city' : 'Add live city'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {formError && <Alert severity="error">{formError}</Alert>}

          <TextField
            label="City name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            helperText="Travellers search guides by this name, so match the city guides enter."
          />
          <TextField
            select
            label="State"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            fullWidth
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {INDIA_STATES.map((state) => (
              <MenuItem key={state} value={state}>
                {state}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Highlight"
            value={highlight}
            onChange={(e) => setHighlight(e.target.value)}
            fullWidth
            inputProps={{ maxLength: 120 }}
            helperText="Short line on the home page card, e.g. “Taj Mahal & Mughal heritage”."
          />
          <TextField
            label="Display order"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            helperText="Lower numbers show first."
            sx={{ maxWidth: 200 }}
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Image
            </Typography>
            {image && (
              <Box
                component="img"
                src={image.url}
                alt="City preview"
                sx={{
                  display: 'block',
                  width: '100%',
                  maxHeight: 220,
                  objectFit: 'cover',
                  borderRadius: 2,
                  mb: 1,
                }}
              />
            )}
            <Button
              variant="outlined"
              size="small"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              sx={{ textTransform: 'none' }}
            >
              {uploading ? 'Uploading…' : image ? 'Replace image' : 'Upload image'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/jpg"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void handleUpload(file);
              }}
            />
          </Box>

          <FormControlLabel
            control={<Switch checked={isLive} onChange={(_, checked) => setIsLive(checked)} />}
            label="Live on home page"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={saveMutation.isPending || uploading}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add city'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
